/**
 * Citation-aware Markdown rendering, shared by every surface that shows an AI
 * answer with `[N]` / `[LN]` markers: the chat bubble (MessageBubble) and the
 * quick-write document panel (ArtifactPanel).
 *
 * It owns the three things that must agree for a citation to be right:
 *   1. which remark plugins run (pending while streaming, clickable once the
 *      sources land, stripped when there is nothing to point at),
 *   2. the display renumbering (first-use order, so `[L12][L3]` reads L1, L2),
 *   3. the react-markdown `components` mapping onto the citation chips.
 *
 * Callers feed `normalizedContent` to ReactMarkdown — never the raw content —
 * because the renumbering is computed from the normalized string (see
 * normalizeCitationMarkdown for why both sides must match).
 */
import { useMemo, type ComponentType } from "react"
import type ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import type { ChatMessage } from "@/stores/assistant-store"
import {
  remarkAttAttributions,
  remarkCitations,
  remarkLibraryCitations,
  remarkPendingCitations,
  remarkStripCitations,
  normalizeCitationMarkdown,
} from "@/lib/remark-citations"
import {
  buildLibraryCitationMapping,
  buildTenderCitationMapping,
  type CitationDisplayMapping,
} from "@/lib/cited-sources"
import { AttAttributionNodeComponent } from "@/components/assistant/AttAttributionChip"
import {
  CitationButton,
  LibraryCitationButton,
  PendingCitationChip,
} from "@/components/assistant/CitationButtons"

type RemarkPlugins = Parameters<typeof ReactMarkdown>[0]["remarkPlugins"]

export interface UseCitationMarkdownOptions {
  content: string
  sources: ChatMessage["sources"]
  librarySources: ChatMessage["librarySources"]
  /**
   * Sources are not final yet (the turn or document is still streaming):
   * markers render as neutral placeholders instead of numbers that would
   * silently change on completion (F42).
   */
  isPending?: boolean
  /**
   * Remove every marker, and the space before it — the "as it will be
   * exported" reading view.
   */
  hideCitations?: boolean
}

export interface UseCitationMarkdownResult {
  /** The string to render AND the string the mapping was computed from. */
  normalizedContent: string
  remarkPlugins: RemarkPlugins
  components: Record<string, ComponentType<Record<string, unknown>>>
  citationMapping: CitationDisplayMapping & { displayToOrig: Map<number, number> }
  libraryCitationMapping: CitationDisplayMapping
  /** Stable key of the tender sources — use it to remount ReactMarkdown when they change. */
  sourcesKey: string
}

export function useCitationMarkdown({
  content,
  sources,
  librarySources,
  isPending = false,
  hideCitations = false,
}: UseCitationMarkdownOptions): UseCitationMarkdownResult {
  const sourceCount = sources?.length ?? 0
  const librarySourceCount = librarySources?.length ?? 0

  // Create stable key from actual source content (not just array reference or length)
  // This ensures ReactMarkdown remounts when sources meaningfully change
  const sourcesKey = sources
    ?.map((s) => `${s.documentId}:${s.pageNumber ?? ''}`)
    .join('|') ?? 'no-sources'

  // Memoize remark plugins to avoid re-creating on every render
  const remarkPlugins = useMemo(() => {
    const plugins: RemarkPlugins = [remarkGfm]
    // Attachment attribution tokens (spec 19). Always on, and always ahead of
    // every citation plugin: those normalize bracket shapes and rewrite text
    // nodes in place, so `[ATT: …]` must be lifted out of the text first or it
    // risks being read as a malformed citation — or, on a zero-source turn,
    // being left behind as a raw token in the prose.
    plugins.push(remarkAttAttributions())
    // Reading view: every marker goes, whatever the sources.
    if (hideCitations) {
      plugins.push(remarkStripCitations())
      return plugins
    }
    // While streaming, sources have not arrived and the model's raw indices
    // are not the final display numbers — render a neutral placeholder rather
    // than a number that would silently change on completion (F42).
    if (isPending) {
      plugins.push(remarkPendingCitations())
      return plugins
    }
    if (sourceCount > 0) {
      plugins.push(remarkCitations(sourceCount))
    }
    if (librarySourceCount > 0) {
      plugins.push(remarkLibraryCitations(librarySourceCount))
    }
    // A finished turn with no sources at all: the markers point at nothing and
    // nothing more is coming, so collapse them rather than leave the model's raw
    // bookkeeping in the prose (IMP-2 — the streamed placeholders must not flip
    // back into literal "[1]" on finalise).
    if (sourceCount === 0 && librarySourceCount === 0) {
      plugins.push(remarkStripCitations())
    }
    return plugins
  }, [sourceCount, librarySourceCount, isPending, hideCitations])

  // One normalized string feeds BOTH parse and render. Normalizing only one
  // side makes inline badge numbers disagree with the rail (first-appearance
  // order changes when a leading marker run moves).
  const normalizedContent = useMemo(
    () => normalizeCitationMarkdown(content),
    [content]
  )

  // Parse content to find which citations are used and create mapping for
  // sequential renumbering (e.g., [1], [2], [3], [5] → [1], [2], [3], [4]).
  // MUST be defined before `components` useMemo since it depends on this.
  // The builders are shared with countCitedSources, so a surface that counts
  // cited sources without rendering (the chat's document chip) agrees with this.
  const citationMapping = useMemo(
    () => buildTenderCitationMapping(normalizedContent, sources),
    [normalizedContent, sources]
  )

  // Parse library citations used in the content
  const libraryCitationMapping = useMemo(
    () => buildLibraryCitationMapping(normalizedContent, librarySources),
    [normalizedContent, librarySources]
  )

  // Custom component mapping for citation nodes
  const components = useMemo(() => {
    const hasSources = sources && sources.length > 0
    const hasLibSources = librarySources && librarySources.length > 0

    // Cast needed: react-markdown's Components type doesn't know about custom elements
    // produced by our remark-citations plugin
    const mapping: Record<string, ComponentType<Record<string, unknown>>> = {}

    // Always mapped, and why there is no early `return undefined` here: the ATT
    // plugin runs on every message, and an unmapped custom element renders as an
    // empty tag — which would swallow the filename entirely rather than show it.
    mapping['att-attribution'] = AttAttributionNodeComponent

    if (isPending) {
      mapping['pending-citation'] = () => <PendingCitationChip />
    }

    if (hasSources) {
      mapping['citation'] = ({ index: origIndex }: { index?: number }) => {
        // Validate original index from markdown
        if (origIndex === undefined || origIndex < 1 || origIndex > sources.length) {
          return <span>[{origIndex}]</span>
        }

        // Get sequential display index from mapping
        const displayIndex = citationMapping.origToDisplay.get(origIndex)
        if (displayIndex === undefined) {
          return <span>[{origIndex}]</span>
        }

        const source = sources[origIndex - 1]
        if (!source) return <span>[{origIndex}]</span>

        return (
          <CitationButton
            displayIndex={displayIndex}
            originalIndex={origIndex}
            documentId={source.documentId}
            documentName={source.documentName}
            pageNumber={source.pageNumber}
            snippet={source.snippet ?? ''}
            {...(source.quote && { quote: source.quote })}
            {...(source.highlightData && { highlightData: source.highlightData })}
          />
        )
      }
    }

    if (hasLibSources) {
      mapping['library-citation'] = ({ index: origIndex }: { index?: number }) => {
        if (origIndex === undefined || origIndex < 1 || origIndex > librarySources.length) {
          return <span>[L{origIndex}]</span>
        }

        const displayIndex = libraryCitationMapping.origToDisplay.get(origIndex)
        if (displayIndex === undefined) {
          return <span>[L{origIndex}]</span>
        }

        const source = librarySources[origIndex - 1]
        if (!source) return <span>[L{origIndex}]</span>

        return (
          <LibraryCitationButton
            displayIndex={displayIndex}
            documentId={source.documentId}
            documentName={source.documentName}
            pageNumber={source.pageNumber}
            snippet={source.snippet}
            {...(source.quote && { quote: source.quote })}
            {...(source.highlightData && { highlightData: source.highlightData })}
          />
        )
      }
    }

    return mapping
  }, [citationMapping, libraryCitationMapping, sources, librarySources, isPending])

  return {
    normalizedContent,
    remarkPlugins,
    components,
    citationMapping,
    libraryCitationMapping,
    sourcesKey,
  }
}
